import { Spacer, VDBText } from "@components/atoms";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import Routes from "@navigations/Routes";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Platform, Pressable, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import styled, { useTheme } from "styled-components/native";
import { ProductTypeProps } from "../SavedSearchTab/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import { VDBIcons } from "@utils/VDBIcons";
import { CoachOverlayStep } from "@components/CoachOverlay";
import { useOverlayConfig } from "@redux/slices/globalSlice";
import { View as ShadowView } from "@gluestack-ui/themed";
import useProductArray from "@components/common/hooks/useProductArray";
import useCollectionNavigation from "@components/common/hooks/useCollectionNavigation";
import { css } from "styled-components/native";
import { navigate } from "@navigations/utils/RootNavigation";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getProductThemeFromType } from "@components/common/Header/utils";
import { getBoxShadow } from "@utils/product/productValidators";
import Animated from "react-native-reanimated";

const ProductTypes = ({}: ProductTypeProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const embedded = Platform.OS === "web" && !!useUserInfo().isIFrame();

  const getOverlayConfig = useOverlayConfig();
  const overlayActiveScreens = useSelector(
    (state) => state.appState.overlayActiveScreens
  );
  const { productArray } = useProductArray();
  const {
    isCollectionOptionClickable,
    navigateToType,
    openCollection,
    selectCollectionOption,
  } = useCollectionNavigation();

  const ItemCard = ({ item }: any) => {
    const productTheme = getProductThemeFromType(item.productType);

    const handleOptionClick = (option: any, event: any) => {
      event.stopPropagation();
      selectCollectionOption(option, {
        productType: item.productType,
        sectionName: item.sectionName,
        productPrefix: item.productPrefix,
        productColor: item.productColor,
        title: item.title,
        icon: item.icon,
        id: item.id,
      });
    };

    const isCardClickable = (_mainItem: any) =>
      isCollectionOptionClickable(_mainItem);

    const handleMainCardClick = () => {
      if (!isCardClickable(item)) return;
      if (item.is_collection && item.options?.length > 0) {
        openCollection(item);
      } else if (
        item?.component === Routes.InternalPagesScreen &&
        item?.iframeUrl
      ) {
        navigate(Routes.InternalPagesScreen, {
          iframeUrl: item.iframeUrl,
        });
      } else {
        navigateToType(item);
      }
    };

    return (
      <Pressable
        onPress={handleMainCardClick}
        testID={`productTypes.mainCard.${item?.productType ?? "unknown"}`}
      >
        <CustomView>
          <CardParent style={{ width: "100%" }}>
            <ProductCard
              softShadow="1"
              displaySubmenu={item?.displaySubmenu && item?.options?.length > 0}
            >
              <CardMain>
                <VDBIcon
                  name={item.icon}
                  uri={item.icon}
                  size={40}
                  color={theme[item.productColor]}
                  variant={
                    item.type === IconsType.img_url
                      ? IconsVariant.CustomUrl
                      : IconsVariant.VDBCustom
                  }
                />
                <VDBText
                  color={
                    item.labelColor
                      ? theme[item.labelColor]
                      : theme[`${productTheme}-06`]
                  }
                  type={typography["H3-M"]}
                >
                  {item.title}
                </VDBText>
              </CardMain>

              {isCardClickable(item) ? (
                <View>
                  <VDBIcon
                    name={VDBIcons.ARROW_RIGHT}
                    size={24}
                    color={theme["core-04"]}
                    variant={IconsVariant.VDBCustom}
                  />
                </View>
              ) : null}
            </ProductCard>

            {item.displaySubmenu && item.options && item.options.length > 0 && (
              <SubmenuContainer>
                {item.options.map((option: any, index: number) => (
                  <TouchableOpacity
                    key={index}
                    onPress={(event) => handleOptionClick(option, event)}
                  >
                    <SubOptionCard isLast={index === item.options.length - 1}>
                      <SubOptionText
                        color={theme["core-05"]}
                        type={typography["Body-R"]}
                      >
                        {option?.label || option?.name}
                      </SubOptionText>
                      <VDBIcon
                        name={VDBIcons.ARROW_RIGHT}
                        size={24}
                        color={theme["core-04"]}
                        variant={IconsVariant.VDBCustom}
                      />
                    </SubOptionCard>
                  </TouchableOpacity>
                ))}
              </SubmenuContainer>
            )}
          </CardParent>
        </CustomView>
      </Pressable>
    );
  };

  return (
    <OverlayContainer
      bottom={overlayActiveScreens.ExploreScreen}
      embedded={embedded}
    >
      <CoachOverlayStep
        screen="ExploreScreen"
        stepKey="productTypes"
        text={
          getOverlayConfig.screens.ExploreScreen?.steps[2]?.text ||
          "Default product types overlay"
        }
        order={3}
        style={{
          justifyContent: "center",
          width: "100%",
          maxWidth: 608,
          ...(Platform.OS === "web" && !embedded ? { height: "100%" } : {}),
        }}
      >
        {embedded ? (
          <View
            style={{
              backgroundColor: theme["core-02"],
              paddingTop: SpaceType.Double,
              width: "100%",
              maxWidth: 608,
              paddingHorizontal: SpaceType.Double,
            }}
          >
            {productArray.map((item: any) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </View>
        ) : (
          <Animated.FlatList
            data={productArray}
            scrollEventThrottle={16}
            renderItem={({ item }: { item: any }) => <ItemCard item={item} />}
            keyExtractor={(item: any) => item.id.toString()}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{
              backgroundColor: theme["core-02"],
              paddingTop: SpaceType.Double,
              width: "100%",
              maxWidth: 608,
              paddingHorizontal: SpaceType.Double,
            }}
          />
        )}
      </CoachOverlayStep>
      {embedded ? null : <Spacer size={SpaceType.Size_80} />}
    </OverlayContainer>
  );
};

const ProductCard = styled(ShadowView)<{ displaySubmenu?: boolean }>`
  background-color: ${({ theme }) => theme["core-01"]};
  width: 100%;
  flex-direction: row;
  align-items: center;
  padding: ${SpaceType.Double}px;
  border-radius: 8px;
  border-bottom-left-radius: ${(props) => (props.displaySubmenu ? "0" : "8px")};
  border-bottom-right-radius: ${(props) =>
    props.displaySubmenu ? "0" : "8px"};
  ${(props) =>
    props.displaySubmenu &&
    css`
      border-bottom-width: 1px;
      border-bottom-color: ${({ theme }) => theme["core-03"]};
    `}
`;

const OverlayContainer = styled.View<{ bottom: boolean; embedded?: boolean }>`
  flex: 1;
  align-items: center;
  background-color: ${({ theme }) => theme["core-02"]};
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      padding: 10px;
    `}
  padding-bottom: ${({ bottom, theme, embedded }) =>
    embedded || bottom
      ? 0
      : Platform.OS === "android"
      ? "60px"
      : Platform.OS === "web" && theme.isLargeScreen
      ? "0"
      : "85px"};
  ${({ theme, embedded }) =>
    !embedded &&
    !theme.isLargeScreen &&
    Platform.OS === "web" &&
    css`
      overflow-y: auto;
    `}
  ${({ embedded }) =>
    embedded &&
    css`
      flex: none;
      overflow: visible;
      touch-action: pan-y;
    `}
`;

const CardMain = styled.View`
  flex-direction: row;
  align-items: center;
  align-content: flex-start;
  flex: 1;
  gap: 16px;
`;

const CardParent = styled.View`
  justify-content: center;
  align-items: center;
`;

const CustomView = styled.View`
  justify-content: center;
  align-items: center;
  margin-bottom: ${SpaceType.Double}px;
  ${({ theme }) =>
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Gentle)};
    `}
`;

const SubmenuContainer = styled.View`
  width: 100%;
  background-color: ${({ theme }) => theme["core-01"]};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
`;

const SubOptionCard = styled.View<{ isLast?: boolean }>`
  background-color: ${({ theme }) => theme["core-01"]};
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${SpaceType.Extra}px ${SpaceType.Double}px;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme["core-03"]};
  position: relative;
`;

const SubOptionText = styled(VDBText)`
  flex: 1;
`;

export default ProductTypes;
